import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFetchEQLFields } from '@modules/common/hooks';
import { formatDateTimeRange } from '@modules/common/utils';
import { ExportEqlResultInput, Mutation } from 'generated/graphql';
import { useCallback, useEffect, useMemo } from 'react';
import { createAnalyticsTracker, EventMap } from 'v2/analytics/events';
import { toAggregatePeriodString } from 'v2/core/entities';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { useGetUser } from 'v2/core/services/user';

const trackDownloadReport = createAnalyticsTracker('downloadReport');

const EXPORT_EQL_RESULT = gql`
  mutation exportEQLResult(
    $tenantID: ID!
    $userID: ID!
    $eqlExpression: String!
    $includeContent: Boolean
    $includeOriginalContent: Boolean
  ) {
    exportEQLResult(
      request: {
        tenantID: $tenantID
        userID: $userID
        eqlExpression: $eqlExpression
        includeContent: $includeContent
        includeOriginalContent: $includeOriginalContent
      }
    ) {
      reportID
    }
  }
`;

export interface UseExportFeedbackViaEmailOptions {
  exportedFrom: EventMap['downloadReport']['from'];
}

export function useExportFeedbackViaEmail({
  exportedFrom,
}: UseExportFeedbackViaEmailOptions) {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const { fields: eqlFields } = useFetchEQLFields();

  const { data: sourceDetailsMap, error: sourceDetailsMapError } =
    useGetSourceDetailsMap();

  const eqlProps = useMemo((): EQLProps | null => {
    return sourceDetailsMap
      ? {
          expressionType: ExpressionType.Records,
          groupBy: {},
          sourceDetailsMap,
          eqlFields,
        }
      : null;
  }, [sourceDetailsMap, eqlFields]);

  useEffect(() => {
    if (sourceDetailsMapError) {
      showErrorNotification({
        contentComponent: 'Something went wrong.',
        props: {},
      });
    }
  }, [sourceDetailsMapError, showErrorNotification]);

  const [exportFeedbackMutation, resp] = useMutation<
    Mutation,
    ExportEqlResultInput
  >(EXPORT_EQL_RESULT, {
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to send email. Please try again later.',
        props: {},
      });
    },
    onCompleted: () => {
      showSuccessNotification({
        contentComponent: 'We will send the feedback to your email shortly.',
        props: {},
      });
    },
  });

  const exportFeedbackViaEmail = useCallback(
    async ({
      queryContainer,
      includeContent = false,
      includeOriginalContent = false,
    }: {
      queryContainer: QueryContainer;
      includeContent?: boolean;
      includeOriginalContent?: boolean;
    }) => {
      const eqlExpression = eqlProps
        ? mapToEQL({
            ...eqlProps,
            whereClause: queryContainer.WhereClause,
            dateRange: queryContainer.DateRange,
          })
        : undefined;

      if (!eqlExpression) {
        return Promise.resolve();
      }

      await exportFeedbackMutation({
        variables: {
          userID,
          tenantID,
          eqlExpression,
          includeContent,
          includeOriginalContent,
        },
      });

      trackDownloadReport({
        eventName: 'Export Feedback CTA',
        type: 'records',
        dateRange: formatDateTimeRange(queryContainer.DateRange),
        aggregatePeriod: toAggregatePeriodString(
          queryContainer.AggregatePeriod,
        ),
        includeFeedbackContent: includeContent,
        includeOriginalFeedbackContent: includeOriginalContent,
        eqlExpression,
        from: exportedFrom,
      });
    },
    [eqlProps, exportFeedbackMutation, userID, tenantID, exportedFrom],
  );

  return [exportFeedbackViaEmail, resp] as const;
}
